/**
 * The one HTTP client for the whole application.
 *
 * Every module calls through this. It carries the single session token and
 * the active company and financial year on every request, so a module never
 * has to think about tenancy — and there is no second client with its own
 * idea of who is signed in.
 */

import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { announceDataChanged } from './dataChanged';

const TOKEN_KEY = 'rsc.token';
const COMPANY_KEY = 'rsc.companyId';
const FY_KEY = 'rsc.fyId';

/** Where the API lives. In development Vite proxies `/api` to the server. */
const baseURL = import.meta.env.VITE_API_BASE_URL ?? '/api';

export const api: AxiosInstance = axios.create({
  baseURL,
  timeout: 60_000,
  headers: { 'Content-Type': 'application/json' },
});

// ---------------------------------------------------------------------------
// Session storage
// ---------------------------------------------------------------------------

export function getToken(): string | null {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}

export function setToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch { /* private browsing — the session simply will not persist */ }
}

export function getScope(): { companyId: number | null; fyId: number | null } {
  try {
    const companyId = Number(localStorage.getItem(COMPANY_KEY)) || null;
    const fyId = Number(localStorage.getItem(FY_KEY)) || null;
    return { companyId, fyId };
  } catch {
    return { companyId: null, fyId: null };
  }
}

export function setScope(companyId: number | null, fyId: number | null): void {
  try {
    if (companyId) localStorage.setItem(COMPANY_KEY, String(companyId));
    else localStorage.removeItem(COMPANY_KEY);
    if (fyId) localStorage.setItem(FY_KEY, String(fyId));
    else localStorage.removeItem(FY_KEY);
  } catch { /* ignore */ }
}

export function clearSession(): void {
  setToken(null);
  setScope(null, null);
}

// ---------------------------------------------------------------------------
// Request: attach identity and scope
// ---------------------------------------------------------------------------

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;

  const { companyId, fyId } = getScope();
  if (companyId) config.headers['x-company-id'] = String(companyId);
  if (fyId) config.headers['x-fy-id'] = String(fyId);

  return config;
});

// ---------------------------------------------------------------------------
// Response: turn API errors into something the UI can show a person
// ---------------------------------------------------------------------------

export interface ApiErrorShape {
  status: number;
  code: string;
  message: string;
  details?: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;

  constructor({ status, code, message, details }: ApiErrorShape) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  get isUnauthorized(): boolean { return this.status === 401; }
  get isForbidden(): boolean { return this.status === 403; }
  get isValidation(): boolean { return this.status === 400 || this.status === 422; }
}

/** Called when the session is rejected, so the shell can show the login page. */
let onSessionExpired: (() => void) | null = null;
export function setSessionExpiredHandler(fn: () => void): void {
  onSessionExpired = fn;
}

/** Any client that sees a 401 calls this, so every module ends the session the same way. */
export function expireSession(): void {
  clearSession();
  onSessionExpired?.();
}

/**
 * Route an API path to the module that serves it.
 *
 * The accounting engine's endpoints live under /api/accounting on the unified
 * server. Its screens were written against the unprefixed paths, so the one
 * mapping lives here rather than being repeated in two hundred call sites.
 */
const ACCOUNTING_PREFIXES = ['/transactions', '/reports', '/gst', '/banking', '/tds', '/operations', '/data-import'];

export function resolveApiPath(path: string): string {
  for (const prefix of ACCOUNTING_PREFIXES) {
    if (path === prefix || path.startsWith(prefix + '/') || path.startsWith(prefix + '?')) {
      return '/accounting' + path;
    }
  }
  return path;
}

api.interceptors.response.use(
  (response) => {
    const method = String(response.config?.method ?? 'get').toLowerCase();
    if (method !== 'get' && method !== 'head') announceDataChanged(response.config?.url);
    return response;
  },
  (error: AxiosError<{ code?: string; message?: string; details?: unknown }>) => {
    if (!error.response) {
      // No response at all: the server is down, or the network dropped.
      return Promise.reject(new ApiError({
        status: 0,
        code: 'NETWORK',
        message: 'Could not reach the server. Check your connection and try again.',
      }));
    }

    const { status, data } = error.response;

    if (status === 401) {
      clearSession();
      onSessionExpired?.();
    }

    return Promise.reject(new ApiError({
      status,
      code: data?.code ?? 'ERROR',
      message: data?.message ?? 'The request failed.',
      details: data?.details,
    }));
  },
);

// ---------------------------------------------------------------------------
// Convenience wrappers
// ---------------------------------------------------------------------------

export async function get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
  const { data } = await api.get<T>(url, { params });
  return data;
}

export async function post<T>(url: string, body?: unknown): Promise<T> {
  const { data } = await api.post<T>(url, body);
  return data;
}

export async function put<T>(url: string, body?: unknown): Promise<T> {
  const { data } = await api.put<T>(url, body);
  return data;
}

export async function patch<T>(url: string, body?: unknown): Promise<T> {
  const { data } = await api.patch<T>(url, body);
  return data;
}

export async function del<T>(url: string, body?: unknown): Promise<T> {
  const { data } = await api.delete<T>(url, { data: body });
  return data;
}
